import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import cN from 'classnames';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import { CollapseList, ICollapseListProps } from '@lion-design/core';
import { GroupAllGuaranteed } from '@components/DetailPage';
import { RootState } from '@Redux/detail/store';
import {
    setGroupDetail,
    setGroupAllCalendar,
    setCalendarGroupsMonth,
    setStationInfo,
    setPricesInfo,
    setFlightInfo,
} from '@Redux/detail/Group/actions';
import {
    setItineraries,
    // setOptionalInfo,
    setNoticeinfo,
} from '@Redux/detail/Itineraries/actions';
import { I_CalendarGroup } from '@Redux/detail/Group/interfaces';
import { ArrowLeft } from '@components/Common/Icons';
import Spinner from '@components/Common/Spinner';
import { changeDetailHistory } from '@utils/utils';
import styles from './styles.module.scss';
import { useTranslation } from 'react-i18next';

interface I_Props {
    slidePageVisible?: any;
}

dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);

const GroupAllList: React.FC<I_Props> = ({ slidePageVisible }) => {
    const [IsCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
    const { CalendarGroups, Schedules, GroupInfo, currentTourID, IsPreview } = useSelector(
        (state: RootState) => state.groupReducer
    );
    const { isLoadingCalendar } = CalendarGroups;

    const { t } = useTranslation();

    const dispatch = useDispatch();

    // 以行程 API (tourinfojson) 中得知最先&最後出發日期，得知最小&最大月份
    const isMaxMonth = (function compareScheduleMax() {
        const MaxMonth = dayjs(Schedules?.AllMaxGoDate).format('YYYY-MM');
        return dayjs(CalendarGroups.month).isSameOrAfter(MaxMonth);
    })();

    const isMinMonth = (function compareScheduleMin() {
        const MinMonth = dayjs(Schedules?.AllMinGoDate).format('YYYY-MM');
        return dayjs(CalendarGroups.month).isSameOrBefore(MinMonth);
    })();

    function clickGroup(data: I_CalendarGroup) {
        const reqBody = {
            GroupID: data.ID,
        };
        const FlightInfoReqBody = {
            GroupID: data.ID,
            NormGroupID: data.NormGroupID,
            GoDate: dayjs(data.Date).format('YYYY-MM-DD'),
        };

        changeDetailHistory({ NormGroupID: data.NormGroupID, GroupID: data.ID });
        dispatch(setGroupDetail(reqBody));
        dispatch(setStationInfo(reqBody));
        // dispatch(setOptionalInfo(reqBody));
        dispatch(setNoticeinfo(reqBody));
        dispatch(setPricesInfo(reqBody));
        dispatch(setFlightInfo(FlightInfoReqBody));
        slidePageVisible && slidePageVisible(setIsCalendarOpen(false));

        // 若點擊團的行程與現在團的行程不相同
        // 將現在團的行程切換到此行程
        (function changeGroupToSameTour() {
            if (data.TourID !== GroupInfo.TourID) {
                const itinerariesReqbody = {
                    TourID: data.TourID,
                    IsPreview: IsPreview,
                    TravelType: GroupInfo.TravelType,
                };

                dispatch(setItineraries(itinerariesReqbody));
            }
        })();
    }

    function clickPrevOrNext(condition: 'prev' | 'next') {
        const month =
            condition === 'prev'
                ? dayjs(CalendarGroups.month).subtract(1, 'month').format('YYYY-MM')
                : dayjs(CalendarGroups.month).add(1, 'month').format('YYYY-MM');
        const [GoDateStart, GoDateEnd] = getMonthStartAndEnd(month);

        const reqBody = {
            NormGroupID: GroupInfo.NormGroupID,
            GoDateStart,
            GoDateEnd,
            TourID: currentTourID,
        };

        dispatch(setCalendarGroupsMonth(month));
        dispatch(setGroupAllCalendar(reqBody));
    }

    // 列表資料格式轉換
    const dataSourceList = CalendarGroups.groups?.map((group: I_CalendarGroup) => {
        const {
            WeekDay,
            Date,
            Guaranteed,
            IsVip,
            CurrencyCode,
            Price,
            VipOrigPrice,
            TotalVacnacy,
        } = group;
        const dataSourceItem = {
            ...group,
            Date: `${Date.replace(/-/g, '/')} (${WeekDay})`,
            IsVip: IsVip && IsVipText,
            Guaranteed: Guaranteed && <GuaranteedTag />,
            MobileLinebreak: '',
            onClick: clickGroup,
            isActive: group.ID === GroupInfo.GroupID,
            Price: PriceContent(IsVip, Price, VipOrigPrice, CurrencyCode),
            TotalVacnacy: <TotalVacnacyContent TotalVacnacy={TotalVacnacy} />,
        };

        return dataSourceItem;
    });

    const guaranteedData = CalendarGroups.groups?.filter((group: I_CalendarGroup) => {
        return group.Guaranteed == true;
    });

    const guaranteedDataList = guaranteedData.map((group: I_CalendarGroup) => {
        const {
            WeekDay,
            Date,
            Guaranteed,
            IsVip,
            CurrencyCode,
            Price,
            VipOrigPrice,
            TotalVacnacy,
        } = group;
        const dataSourceItem = {
            ...group,
            Date: `${Date.replace(/-/g, '/')} (${WeekDay})`,
            IsVip: IsVip && IsVipText,
            Guaranteed: Guaranteed && <GuaranteedTag />,
            MobileLinebreak: '',
            onClick: clickGroup,
            isActive: group.ID === GroupInfo.GroupID,
            Price: PriceContent(IsVip, Price, VipOrigPrice, CurrencyCode),
            TotalVacnacy: <TotalVacnacyContent TotalVacnacy={TotalVacnacy} />,
        };

        return dataSourceItem;
    });

    const titles: ICollapseListProps['titles'] = [
        {
            name: '出發日期',
            key: 'Date',
            size: '21%',
        },
        {
            name: '行程',
            key: 'ScheduleType',
            size: '6%',
        },
        {
            name: '',
            key: 'Guaranteed',
            size: '6%',
        },
        {
            name: '',
            key: 'IsVip',
            size: '14%',
        },
        {
            name: `${t('IsEnsureGroup')}狀態`,
            key: 'Status',
            size: '14%',
        },
        {
            name: '',
            key: 'MobileLinebreak',
            size: '0%',
        },
        {
            name: '可賣',
            key: 'AvailableVacancy',
            size: '9%',
        },
        {
            name: t('seats'),
            key: 'TotalVacnacy',
            size: '9%',
        },
        {
            name: '價格',
            key: 'Price',
            size: '21%',
        },
    ];

    return (
        <div className={cN(styles['container_m'])}>
            {Schedules?.TourIDList && <span className={styles.mTitle}>選擇出發日期</span>}
            <div
                className={cN(styles.container, 'group_all_list', {
                    [`${styles.show}`]: CalendarGroups.isListMode,
                })}
            >
                <div className={styles.switcher}>
                    <span className={styles['switcher_text']}>
                        {dayjs(CalendarGroups.month).format('YYYY年M月')}
                    </span>
                    <GroupAllGuaranteed />
                    <span
                        className={styles['switcher_prev']}
                        onClick={isMinMonth ? undefined : () => clickPrevOrNext('prev')}
                    >
                        <ArrowLeft fill={isMinMonth ? '#bbb' : '#669'} />
                    </span>
                    <span
                        className={styles['switcher_next']}
                        onClick={isMaxMonth ? undefined : () => clickPrevOrNext('next')}
                    >
                        <ArrowLeft fill={isMaxMonth ? '#bbb' : '#669'} />
                    </span>
                </div>
                {isLoadingCalendar ? (
                    <Spinner />
                ) : CalendarGroups.groups?.length > 0 ? (
                    <CollapseList
                        className={styles.groups}
                        titles={titles}
                        dataSource={
                            !CalendarGroups.isGuaranteedDisabled &&
                            CalendarGroups.parmIsEnsureGroup &&
                            CalendarGroups.isGuaranteedChecked
                                ? guaranteedDataList
                                : CalendarGroups.groups?.length > 0
                                ? dataSourceList
                                : []
                        }
                    />
                ) : (
                    <span className={styles.noData}>本月無出發日，請切換其他月份</span>
                )}
            </div>
        </div>
    );
};

export default GroupAllList;

/*******************/

function PriceContent(isVip: boolean, price: any, vipPrice: any, currencyCode: string) {
    function changeprice(price: number | string) {
        return `${price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;
    }

    return (
        <>
            {price && (
                <span className={styles.price}>
                    {currencyCode || ''}&ensp;{price}
                </span>
            )}
            {isVip && (
                <span className={styles.vipPrice}>
                    原價：{currencyCode || ''}&ensp;{changeprice(vipPrice)}
                </span>
            )}
        </>
    );
}

function TotalVacnacyContent(props: { TotalVacnacy: number }) {
    const { t } = useTranslation();

    return (
        <>
            <span className={styles.label}>{t('seats')}</span>
            {props.TotalVacnacy}
        </>
    );
}

const IsVipText = <div className={styles.isVip}>會員限購</div>;

function GuaranteedTag() {
    const { t } = useTranslation();
    return <div className={styles.guaranteed}>{t('IsEnsureGroup')}</div>;
}

function getMonthStartAndEnd(day: string) {
    const start = dayjs(day).startOf('month').format('YYYY-MM-DD');
    const end = dayjs(day).endOf('month').format('YYYY-MM-DD');

    return [start, end];
}
